import { useCallback, useState, useEffect, type MutableRefObject, useRef } from 'react';
import {
	fullscreenElement,
	requestFullscreen,
	exitFullscreen,
	addEventListener,
	removeEventListener
} from '~/utils/fullscreen';

export interface FullScreenHandle {
	active: boolean;
	enter: () => Promise<void>;
	exit: () => Promise<void>;
}

export function useFullScreen(node?: MutableRefObject<HTMLElement | null>): FullScreenHandle {
	const [active, setActive] = useState<boolean>(false);

	const targetRef = useRef<HTMLElement | null>(node?.current || document.documentElement);

	useEffect(() => {
		const handleChange = () => {
			setActive(fullscreenElement() === targetRef.current);
		};
		addEventListener(handleChange);
		return () => {
			removeEventListener(handleChange);
		};
	}, []);

	const enter = useCallback(async () => {
		if (!targetRef.current) return;

		if (fullscreenElement()) {
			await exitFullscreen();
		}
		return requestFullscreen(targetRef.current);
	}, []);

	const exit = useCallback(() => {
		if (fullscreenElement() === targetRef.current) {
			return exitFullscreen();
		}
		return Promise.resolve();
	}, []);

	return {
		active,
		enter,
		exit
	};
}
